import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type MouseEvent,
  type DragEvent,
} from "react";
import { useLocation, useParams } from "@tanstack/react-router";
import { useAction } from "convex/react";
import { useAuthToken } from "@convex-dev/auth/react";
import {
  ChevronRight,
  ChevronDown,
  Clock,
  Folder,
  FolderOpen,
  FileText,
  FilePlus,
  FolderPlus,
  RefreshCw,
  MoreVertical,
  Pencil,
  Trash2,
  Undo2,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { api } from "@/lib/convex-api";
import { formatUserFacingError } from "@/lib/errorMessages";
import {
  filterRecentFilesByExisting,
  formatExactDateTime,
  formatFriendlyTime,
  parseFilePathDisplay,
} from "@/features/project/lib/recentFilesHelpers";
import { useProjectUIStore } from "@/features/project/stores/project-ui-store";
import type { FileNode } from "@/features/project/types";
import {
  isValidDropTarget,
  computeMoveOperations,
} from "@/features/project/lib/file-move-helpers";
import { toast } from "sonner";

/**
 * Module-level variable to track the node currently being dragged.
 * This avoids prop-drilling and works because HTML5 drag-and-drop
 * is synchronous within a single browser tab.
 */
let currentDraggedNode: FileNode | null = null;

interface FileTreeProps {
  nodes: FileNode[];
  /** When provided, shows a sync button in the header */
  syncButton?: {
    onSync: () => void;
    isSyncing: boolean;
    /** Provider-specific label for aria-label, e.g. "Sync from GitHub" */
    ariaLabel?: string;
  };
}

export const FileTree = ({ nodes, syncButton }: FileTreeProps) => {
  const location = useLocation();
  const routeParams = useParams({ strict: false });
  const [creatingAt, setCreatingAt] = useState<{
    type: "file" | "folder";
    parentPath: string | null;
  } | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [renamingAt, setRenamingAt] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const projectId = useProjectUIStore((s) => s.projectId);
  const selectFile = useProjectUIStore((s) => s.selectFile);
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const token = useAuthToken();
  const writeFile = useAction(api.projectFiles.write);
  const deleteFileAction = useAction(api.projectFiles.deleteFile);
  const restoreDeletedFileAction = useAction(api.projectFiles.restoreDeleted);
  const moveFileAction = useAction(api.projectFiles.move);
  const [isMoving, setIsMoving] = useState(false);
  const [rootDropHighlight, setRootDropHighlight] = useState(false);
  const rootDragOverCounter = useRef(0);
  const [virtualFolderPaths, setVirtualFolderPaths] = useState<string[]>([]);
  const virtualFoldersStorageKey = projectId
    ? `repowrite-virtual-folders-${projectId}`
    : null;

  useEffect(() => {
    if (!virtualFoldersStorageKey) {
      setVirtualFolderPaths([]);
      return;
    }
    try {
      const raw = globalThis.localStorage?.getItem(virtualFoldersStorageKey);
      if (!raw) {
        setVirtualFolderPaths([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setVirtualFolderPaths([]);
        return;
      }
      setVirtualFolderPaths(
        parsed
          .filter((v): v is string => typeof v === "string")
          .map((v) => normalizeRelativePath(v))
          .filter(Boolean),
      );
    } catch {
      setVirtualFolderPaths([]);
    }
  }, [virtualFoldersStorageKey]);

  const persistVirtualFolders = useCallback(
    (nextPaths: string[]) => {
      setVirtualFolderPaths(nextPaths);
      if (!virtualFoldersStorageKey) return;
      try {
        globalThis.localStorage?.setItem(
          virtualFoldersStorageKey,
          JSON.stringify(nextPaths),
        );
      } catch {
        // ignore
      }
    },
    [virtualFoldersStorageKey],
  );

  const nodesWithVirtualFolders = useMemo(
    () => mergeVirtualFolders(nodes, virtualFolderPaths),
    [nodes, virtualFolderPaths],
  );
  const sorted = sortNodes(nodesWithVirtualFolders);
  const rootFolders = useMemo(
    () => sorted.filter((node) => node.type === "folder"),
    [sorted],
  );
  const rootFiles = useMemo(
    () => sorted.filter((node) => node.type === "file"),
    [sorted],
  );
  const dirtyPathSet = useMemo(() => collectDirtyFilePaths(nodes), [nodes]);
  const deletedPathSet = useMemo(() => collectDeletedFilePaths(nodes), [nodes]);
  const existingFilePaths = useMemo(() => collectAllFilePaths(nodes), [nodes]);
  const getFileHref = useCallback(
    (filePath: string) => {
      const orgSlug = (routeParams.orgSlug as string | undefined) ?? "";
      const projectSlug = (routeParams.id as string | undefined) ?? "";
      const encodedFilePath = filePath
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");
      const basePath =
        orgSlug && projectSlug
          ? `/${encodeURIComponent(orgSlug)}/${encodeURIComponent(projectSlug)}`
          : location.pathname.endsWith("/select")
            ? location.pathname.slice(0, -"/select".length)
            : location.pathname;
      const searchParams = new URLSearchParams(location.searchStr);
      searchParams.delete("file");
      const nextSearch = searchParams.toString();
      return `${basePath}/${encodedFilePath}${nextSearch ? `?${nextSearch}` : ""}`;
    },
    [
      location.pathname,
      location.searchStr,
      routeParams.id,
      routeParams.orgSlug,
    ],
  );
  const handleCreateSubmit = useCallback(
    async (enteredName: string) => {
      if (!creatingAt) return;
      if (!projectId || !token) {
        toast.error(
          "Could not complete that action. Please refresh and try again.",
        );
        setCreatingAt(null);
        return;
      }

      const normalizedParent = normalizeRelativePath(
        creatingAt.parentPath ?? "",
      );
      const normalizedName = normalizeRelativePath(enteredName);
      if (!normalizedName) {
        setCreatingAt(null);
        return;
      }

      const scopedPath = [normalizedParent, normalizedName]
        .filter(Boolean)
        .join("/");
      if (creatingAt.type === "folder") {
        const nextFolders = new Set(virtualFolderPaths);
        for (const folderPath of expandFolderPathSegments(scopedPath)) {
          nextFolders.add(folderPath);
        }
        persistVirtualFolders([...nextFolders]);
        setCreatingAt(null);
        return;
      }
      const targetPath = coerceToMarkdownPath(scopedPath);

      setIsCreating(true);
      try {
        await writeFile({
          projectId: projectId as any,
          token,
          path: targetPath,
          content: "",
        });
        selectFile(targetPath);
      } catch (err) {
        toast.error(formatUserFacingError(err, "Failed to create file."));
      } finally {
        setIsCreating(false);
        setCreatingAt(null);
      }
    },
    [
      creatingAt,
      projectId,
      token,
      writeFile,
      selectFile,
      virtualFolderPaths,
      persistVirtualFolders,
    ],
  );

  const handleRenameSubmit = useCallback(
    async (fromPath: string, newName: string) => {
      if (!projectId || !token) {
        toast.error(
          "Could not complete that action. Please refresh and try again.",
        );
        setRenamingAt(null);
        return;
      }
      const dirPath = fromPath.includes("/")
        ? fromPath.slice(0, fromPath.lastIndexOf("/"))
        : "";
      const newPath = coerceToMarkdownPath(
        dirPath ? `${dirPath}/${newName}` : newName,
      );
      if (newPath === fromPath) {
        setRenamingAt(null);
        return;
      }
      setIsRenaming(true);
      try {
        await moveFileAction({
          projectId: projectId as any,
          token,
          fromPath,
          toPath: newPath,
        });
        selectFile(newPath);
      } catch (err) {
        toast.error(formatUserFacingError(err, "Failed to rename file."));
      } finally {
        setIsRenaming(false);
        setRenamingAt(null);
      }
    },
    [projectId, token, moveFileAction, selectFile],
  );

  const handleDeleteRequest = useCallback(
    async (path: string) => {
      if (!projectId || !token) return;
      setIsDeleting(true);
      try {
        await deleteFileAction({
          projectId: projectId as any,
          token,
          path,
        });
        if (selectedFilePath === path) {
          selectFile(null);
        }
      } catch (err) {
        toast.error(formatUserFacingError(err, "Failed to delete file."));
      } finally {
        setIsDeleting(false);
      }
    },
    [projectId, token, deleteFileAction, selectedFilePath, selectFile],
  );

  const handleUndelete = useCallback(
    async (path: string) => {
      if (!projectId || !token) {
        toast.error(
          "Could not complete that action. Please refresh and try again.",
        );
        return;
      }
      try {
        const result = await restoreDeletedFileAction({
          projectId: projectId as any,
          token,
          path,
        });
        if (!result.restored) {
          toast.error("File is no longer available to restore.");
          return;
        }
      } catch (err) {
        toast.error(formatUserFacingError(err, "Failed to restore file."));
      }
    },
    [projectId, token, restoreDeletedFileAction],
  );

  const handleMoveByDrop = useCallback(
    async (targetFolderPath: string | null) => {
      const draggedNode = currentDraggedNode;
      if (!draggedNode || !projectId || !token) return;

      const moves = computeMoveOperations(draggedNode, targetFolderPath);
      if (moves.length === 0) return;

      setIsMoving(true);
      try {
        for (const move of moves) {
          await moveFileAction({
            projectId: projectId as any,
            token,
            fromPath: move.fromPath,
            toPath: move.toPath,
          });
        }
        if (selectedFilePath) {
          const movedSelected = moves.find(
            (m) => m.fromPath === selectedFilePath,
          );
          if (movedSelected) {
            selectFile(movedSelected.toPath);
          }
        }
      } catch (err) {
        toast.error(formatUserFacingError(err, "Failed to move file."));
      } finally {
        setIsMoving(false);
      }
    },
    [projectId, token, moveFileAction, selectedFilePath, selectFile],
  );

  const handleRootDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!currentDraggedNode) return;
    if (
      !isValidDropTarget(currentDraggedNode.path, currentDraggedNode.type, null)
    )
      return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleRootDragEnter = useCallback(() => {
    if (!currentDraggedNode) return;
    if (
      !isValidDropTarget(currentDraggedNode.path, currentDraggedNode.type, null)
    )
      return;
    rootDragOverCounter.current += 1;
    setRootDropHighlight(true);
  }, []);

  const handleRootDragLeave = useCallback(() => {
    rootDragOverCounter.current -= 1;
    if (rootDragOverCounter.current <= 0) {
      rootDragOverCounter.current = 0;
      setRootDropHighlight(false);
    }
  }, []);

  const handleRootDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      rootDragOverCounter.current = 0;
      setRootDropHighlight(false);
      void handleMoveByDrop(null);
      currentDraggedNode = null;
    },
    [handleMoveByDrop],
  );

  return (
    <>
      <div className="flex h-full flex-col">
        {/* Root-level header */}
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Files
          </span>
          <div className="flex items-center gap-0.5">
            {syncButton && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={syncButton.onSync}
                disabled={syncButton.isSyncing}
                aria-label={syncButton.ariaLabel ?? "Sync from remote"}
              >
                <RefreshCw
                  className={`size-3.5 ${syncButton.isSyncing ? "animate-spin" : ""}`}
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setCreatingAt({ type: "file", parentPath: null })}
            >
              <FilePlus className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() =>
                setCreatingAt({ type: "folder", parentPath: null })
              }
            >
              <FolderPlus className="size-3.5" />
            </Button>
          </div>
        </div>

        {/* Recent files */}
        <RecentFilesSection
          dirtyPathSet={dirtyPathSet}
          deletedPathSet={deletedPathSet}
          existingFilePaths={existingFilePaths}
          getFileHref={getFileHref}
        />

        {/* Tree body */}
        <ScrollArea className="flex-1">
          <div
            className={cn(
              "py-1",
              rootDropHighlight &&
                "bg-primary/5 outline-dashed outline-1 -outline-offset-2 outline-primary/40",
            )}
            onDragOver={handleRootDragOver}
            onDragEnter={handleRootDragEnter}
            onDragLeave={handleRootDragLeave}
            onDrop={handleRootDrop}
          >
            {creatingAt?.parentPath === null &&
              creatingAt.type === "folder" && (
                <InlineInput
                  type="folder"
                  depth={0}
                  isSubmitting={isCreating}
                  onSubmit={(name) => {
                    void handleCreateSubmit(name);
                  }}
                  onCancel={() => setCreatingAt(null)}
                />
              )}
            {rootFolders.map((node) => (
              <TreeNode
                key={node.path}
                node={node}
                depth={0}
                defaultExpanded={rootFolders.length === 1}
                creatingAt={creatingAt}
                isCreating={isCreating}
                onCreateSubmit={(name) => {
                  void handleCreateSubmit(name);
                }}
                setCreatingAt={setCreatingAt}
                getFileHref={getFileHref}
                renamingAt={renamingAt}
                setRenamingAt={setRenamingAt}
                onRenameSubmit={handleRenameSubmit}
                onDeleteRequest={(path) => void handleDeleteRequest(path)}
                onUndeleteRequest={(path) => {
                  void handleUndelete(path);
                }}
                isRenaming={isRenaming}
                onMoveByDrop={handleMoveByDrop}
              />
            ))}
            {creatingAt?.parentPath === null && creatingAt.type === "file" && (
              <InlineInput
                type="file"
                depth={0}
                isSubmitting={isCreating}
                onSubmit={(name) => {
                  void handleCreateSubmit(name);
                }}
                onCancel={() => setCreatingAt(null)}
              />
            )}
            {rootFiles.map((node) => (
              <TreeNode
                key={node.path}
                node={node}
                depth={0}
                creatingAt={creatingAt}
                isCreating={isCreating}
                onCreateSubmit={(name) => {
                  void handleCreateSubmit(name);
                }}
                setCreatingAt={setCreatingAt}
                getFileHref={getFileHref}
                renamingAt={renamingAt}
                setRenamingAt={setRenamingAt}
                onRenameSubmit={handleRenameSubmit}
                onDeleteRequest={(path) => void handleDeleteRequest(path)}
                onUndeleteRequest={(path) => {
                  void handleUndelete(path);
                }}
                isRenaming={isRenaming}
                onMoveByDrop={handleMoveByDrop}
              />
            ))}
          </div>
        </ScrollArea>
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */

const RECENT_ITEM_HEIGHT = 26; // px per row (py-0.5 + text-sm ≈ 26px)
const RECENT_MAX_VISIBLE = 5;
const RECENT_FILES_EXPANDED_KEY = "repowrite-recent-files-expanded";

const RecentFilesSection = ({
  dirtyPathSet,
  deletedPathSet,
  existingFilePaths,
  getFileHref,
}: {
  dirtyPathSet: Set<string>;
  deletedPathSet: Set<string>;
  existingFilePaths: Set<string>;
  getFileHref: (filePath: string) => string;
}) => {
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = globalThis.localStorage?.getItem(
        RECENT_FILES_EXPANDED_KEY,
      );
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });
  const recentFiles = useProjectUIStore((s) => s.recentFiles);
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const selectFile = useProjectUIStore((s) => s.selectFile);

  const visibleRecentFiles = filterRecentFilesByExisting(
    recentFiles,
    existingFilePaths,
  );
  if (visibleRecentFiles.length === 0) return null;

  return (
    <div className="border-b">
      {/* Section header */}
      <button
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-accent/50"
        onClick={() =>
          setExpanded((prev) => {
            const next = !prev;
            try {
              globalThis.localStorage?.setItem(
                RECENT_FILES_EXPANDED_KEY,
                String(next),
              );
            } catch {
              // ignore
            }
            return next;
          })
        }
      >
        <Clock className="size-3 shrink-0" />
        Recent
        {expanded ? (
          <ChevronDown className="ml-auto size-3 shrink-0" />
        ) : (
          <ChevronRight className="ml-auto size-3 shrink-0" />
        )}
      </button>

      {/* File list — max 5 visible, scrollable */}
      {expanded && (
        <ScrollArea
          className="overflow-y-auto"
          style={{
            maxHeight: `${RECENT_ITEM_HEIGHT * RECENT_MAX_VISIBLE}px`,
          }}
        >
          <div className="py-0.5">
            {visibleRecentFiles.map((entry) => {
              const { path } = entry;
              const { fileName, dirPath } = parseFilePathDisplay(path);
              const isSelected = selectedFilePath === path;
              const hasDirtyBadge = dirtyPathSet.has(path);
              const isDeleted = deletedPathSet.has(path);
              return (
                <a
                  key={path}
                  href={getFileHref(path)}
                  className={cn(
                    "flex cursor-pointer items-center gap-1.5 px-2 py-0.5 text-sm hover:bg-accent/50",
                    isSelected && "bg-accent text-accent-foreground",
                  )}
                  style={{ paddingLeft: "20px" }}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    selectFile(path);
                  }}
                  title={path}
                >
                  <span className="relative shrink-0">
                    <FileText className="size-3.5 text-muted-foreground" />
                    {hasDirtyBadge ? (
                      <span
                        className={cn(
                          "absolute -right-0.5 -top-0.5 block size-2 rounded-full ring-2 ring-background",
                          isDeleted ? "bg-red-500" : "bg-amber-500",
                        )}
                      />
                    ) : null}
                  </span>
                  <span className="truncate">{fileName}</span>
                  {dirPath ? (
                    <span className="truncate text-[10px] text-muted-foreground/60">
                      {dirPath}
                    </span>
                  ) : null}
                  <span
                    className="ml-auto shrink-0 text-[10px] text-muted-foreground/60"
                    title={formatExactDateTime(entry.lastAccessedAt)}
                  >
                    {formatFriendlyTime(entry.lastAccessedAt)}
                  </span>
                </a>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface TreeNodeProps {
  node: FileNode;
  depth: number;
  creatingAt: { type: "file" | "folder"; parentPath: string | null } | null;
  isCreating: boolean;
  onCreateSubmit: (name: string) => void;
  getFileHref: (filePath: string) => string;
  setCreatingAt: (
    v: { type: "file" | "folder"; parentPath: string | null } | null,
  ) => void;
  renamingAt: string | null;
  setRenamingAt: (v: string | null) => void;
  onRenameSubmit: (fromPath: string, newName: string) => void;
  onDeleteRequest: (path: string) => void;
  onUndeleteRequest: (path: string) => void;
  isRenaming: boolean;
  onMoveByDrop: (targetFolderPath: string | null) => Promise<void>;
  /** When true, folder starts expanded. Used for single root folder. */
  defaultExpanded?: boolean;
}

const TreeNode = ({
  node,
  depth,
  creatingAt,
  isCreating,
  onCreateSubmit,
  getFileHref,
  setCreatingAt,
  renamingAt,
  setRenamingAt,
  onRenameSubmit,
  onDeleteRequest,
  onUndeleteRequest,
  isRenaming,
  onMoveByDrop,
  defaultExpanded = false,
}: TreeNodeProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const selectedFilePath = useProjectUIStore((s) => s.selectedFilePath);
  const selectFile = useProjectUIStore((s) => s.selectFile);

  // Drag-and-drop local state
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const dragOverCounter = useRef(0);
  const autoExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoExpandTimerRef.current) {
        clearTimeout(autoExpandTimerRef.current);
      }
    };
  }, []);

  const isFolder = node.type === "folder";
  const isSelected = selectedFilePath === node.path;
  const containsSelected =
    isFolder && selectedFilePath?.startsWith(node.path + "/");
  const hasDirtyBadge = isFolder
    ? Boolean(node.hasDirtyDescendant)
    : Boolean(node.isDirty);
  const isDeleted = !isFolder && Boolean(node.isDeleted);
  const [hovered, setHovered] = useState(false);
  const showActions = hovered || containsSelected;

  const sorted = isFolder && node.children ? sortNodes(node.children) : [];
  const childFolders = sorted.filter((child) => child.type === "folder");
  const childFiles = sorted.filter((child) => child.type === "file");
  const hasChildren = sorted.length > 0;

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (isFolder) {
        if (!hasChildren) {
          return;
        }
        setExpanded((prev) => !prev);
      } else {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        selectFile(node.path);
      }
    },
    [isFolder, hasChildren, node.path, selectFile, selectedFilePath],
  );

  return (
    <div>
      {isFolder ? (
        <div
          draggable={!creatingAt && !renamingAt}
          onDragStart={(e) => {
            e.stopPropagation();
            currentDraggedNode = node;
            setIsDragging(true);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", node.path);
          }}
          onDragEnd={() => {
            currentDraggedNode = null;
            setIsDragging(false);
          }}
          onDragOver={(e) => {
            if (!currentDraggedNode) return;
            if (
              !isValidDropTarget(
                currentDraggedNode.path,
                currentDraggedNode.type,
                node.path,
              )
            )
              return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "move";
          }}
          onDragEnter={(e) => {
            if (!currentDraggedNode) return;
            if (
              !isValidDropTarget(
                currentDraggedNode.path,
                currentDraggedNode.type,
                node.path,
              )
            )
              return;
            e.stopPropagation();
            dragOverCounter.current += 1;
            setIsDropTarget(true);
            // Auto-expand collapsed folders after hovering for 600ms
            if (!expanded && dragOverCounter.current === 1) {
              autoExpandTimerRef.current = setTimeout(
                () => setExpanded(true),
                600,
              );
            }
          }}
          onDragLeave={(e) => {
            e.stopPropagation();
            dragOverCounter.current -= 1;
            if (dragOverCounter.current <= 0) {
              dragOverCounter.current = 0;
              setIsDropTarget(false);
              if (autoExpandTimerRef.current) {
                clearTimeout(autoExpandTimerRef.current);
                autoExpandTimerRef.current = null;
              }
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragOverCounter.current = 0;
            setIsDropTarget(false);
            if (autoExpandTimerRef.current) {
              clearTimeout(autoExpandTimerRef.current);
              autoExpandTimerRef.current = null;
            }
            void onMoveByDrop(node.path);
            currentDraggedNode = null;
          }}
          className={cn(
            "group flex cursor-pointer items-center gap-1 px-2 py-0.5 text-sm hover:bg-accent/50",
            isSelected && "bg-accent text-accent-foreground",
            isDeleted && "text-muted-foreground/70",
            isDragging && "opacity-50",
            isDropTarget &&
              "bg-primary/10 outline-dashed outline-1 -outline-offset-1 outline-primary/50",
          )}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          onClick={handleClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Expand icon or spacer */}
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
            )
          ) : (
            <span className="size-3.5 shrink-0" />
          )}

          {/* Node icon */}
          <span className="relative shrink-0">
            {expanded ? (
              <FolderOpen className="size-4 text-muted-foreground" />
            ) : (
              <Folder className="size-4 text-muted-foreground" />
            )}
            {hasDirtyBadge ? (
              <span
                className={cn(
                  "absolute -right-0.5 -top-0.5 block size-2 rounded-full ring-2 ring-background",
                  isDeleted ? "bg-red-500" : "bg-amber-500",
                )}
              />
            ) : null}
          </span>

          {/* Name */}
          <span className="truncate">{node.name}</span>

          {/* Folder action buttons */}
          {showActions && (
            <span className="ml-auto flex shrink-0 gap-0.5">
              <button
                className="rounded p-0.5 hover:bg-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatingAt({ type: "file", parentPath: node.path });
                  setExpanded(true);
                }}
              >
                <FilePlus className="size-3" />
              </button>
              <button
                className="rounded p-0.5 hover:bg-accent"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreatingAt({ type: "folder", parentPath: node.path });
                  setExpanded(true);
                }}
              >
                <FolderPlus className="size-3" />
              </button>
            </span>
          )}
        </div>
      ) : renamingAt === node.path ? (
        <RenameInlineInput
          depth={depth}
          currentName={node.name}
          isSubmitting={isRenaming}
          onSubmit={(name) => {
            void onRenameSubmit(node.path, name);
          }}
          onCancel={() => setRenamingAt(null)}
        />
      ) : (
        <div
          draggable={!isDeleted}
          onDragStart={(e) => {
            e.stopPropagation();
            currentDraggedNode = node;
            setIsDragging(true);
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", node.path);
          }}
          onDragEnd={() => {
            currentDraggedNode = null;
            setIsDragging(false);
          }}
          className={cn(
            "group flex cursor-pointer items-center gap-1 px-2 py-0.5 text-sm hover:bg-accent/50",
            isSelected && "bg-accent text-accent-foreground",
            isDragging && "opacity-50",
          )}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <a
            draggable={false}
            href={getFileHref(node.path)}
            className="flex min-w-0 flex-1 items-center gap-1"
            onClick={(e) => {
              if (
                e.button !== 0 ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey
              ) {
                return;
              }
              e.preventDefault();
              selectFile(node.path);
            }}
            title={node.path}
          >
            <span className="size-3.5 shrink-0" />
            <span className="relative shrink-0">
              <FileText className="size-4 text-muted-foreground" />
              {hasDirtyBadge ? (
                <span
                  className={cn(
                    "absolute -right-0.5 -top-0.5 block size-2 rounded-full ring-2 ring-background",
                    isDeleted ? "bg-red-500" : "bg-amber-500",
                  )}
                />
              ) : null}
            </span>
            <span className={cn("truncate", isDeleted && "line-through")}>
              {node.name}
            </span>
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                className={cn(
                  "shrink-0 rounded p-0.5",
                  !showActions && "opacity-0 group-hover:opacity-100",
                )}
                onClick={(e) => e.stopPropagation()}
                aria-label="File options"
              >
                <MoreVertical className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {isDeleted ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onUndeleteRequest(node.path);
                  }}
                >
                  <Undo2 className="mr-2 size-3.5" />
                  Undelete
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenamingAt(node.path);
                    }}
                  >
                    <Pencil className="mr-2 size-3.5" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteRequest(node.path);
                    }}
                  >
                    <Trash2 className="mr-2 size-3.5" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Children */}
      {isFolder && expanded && (
        <div>
          {creatingAt?.parentPath === node.path &&
            creatingAt.type === "folder" && (
              <InlineInput
                type="folder"
                depth={depth + 1}
                isSubmitting={isCreating}
                onSubmit={(name) => {
                  onCreateSubmit(name);
                }}
                onCancel={() => setCreatingAt(null)}
              />
            )}
          {childFolders.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              creatingAt={creatingAt}
              isCreating={isCreating}
              onCreateSubmit={onCreateSubmit}
              getFileHref={getFileHref}
              setCreatingAt={setCreatingAt}
              renamingAt={renamingAt}
              setRenamingAt={setRenamingAt}
              onRenameSubmit={onRenameSubmit}
              onDeleteRequest={onDeleteRequest}
              onUndeleteRequest={onUndeleteRequest}
              isRenaming={isRenaming}
              onMoveByDrop={onMoveByDrop}
            />
          ))}
          {creatingAt?.parentPath === node.path &&
            creatingAt.type === "file" && (
              <InlineInput
                type="file"
                depth={depth + 1}
                isSubmitting={isCreating}
                onSubmit={(name) => {
                  onCreateSubmit(name);
                }}
                onCancel={() => setCreatingAt(null)}
              />
            )}
          {childFiles.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              creatingAt={creatingAt}
              isCreating={isCreating}
              onCreateSubmit={onCreateSubmit}
              getFileHref={getFileHref}
              setCreatingAt={setCreatingAt}
              renamingAt={renamingAt}
              setRenamingAt={setRenamingAt}
              onRenameSubmit={onRenameSubmit}
              onDeleteRequest={onDeleteRequest}
              onUndeleteRequest={onUndeleteRequest}
              isRenaming={isRenaming}
              onMoveByDrop={onMoveByDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface RenameInlineInputProps {
  depth: number;
  currentName: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const RenameInlineInput = ({
  depth,
  currentName,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: RenameInlineInputProps) => {
  const [value, setValue] = useState(currentName);

  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5"
      style={{ paddingLeft: `${depth * 12 + 8 + 18}px` }}
    >
      <FileText className="size-4 shrink-0 text-muted-foreground" />
      <Input
        autoFocus
        className="h-6 rounded-sm border-none px-1 py-0 text-sm shadow-none focus-visible:ring-1"
        placeholder="file name"
        disabled={isSubmitting}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (isSubmitting) return;
          if (e.key === "Enter" && value.trim()) {
            e.preventDefault();
            e.stopPropagation();
            onSubmit(value.trim());
          }
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        onBlur={onCancel}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface InlineInputProps {
  type: "file" | "folder";
  depth: number;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const InlineInput = ({
  type,
  depth,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: InlineInputProps) => {
  const [value, setValue] = useState("");

  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5"
      style={{ paddingLeft: `${depth * 12 + 8 + 18}px` }}
    >
      {type === "folder" ? (
        <Folder className="size-4 shrink-0 text-muted-foreground" />
      ) : (
        <FileText className="size-4 shrink-0 text-muted-foreground" />
      )}
      <Input
        autoFocus
        className="h-6 rounded-sm border-none px-1 py-0 text-sm shadow-none focus-visible:ring-1"
        placeholder={type === "folder" ? "folder name" : "file name"}
        disabled={isSubmitting}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (isSubmitting) return;
          if (e.key === "Enter" && value.trim()) {
            e.preventDefault();
            e.stopPropagation();
            onSubmit(value.trim());
          }
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        onBlur={onCancel}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */

function sortNodes(nodes: FileNode[]): FileNode[] {
  return [...nodes].sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function normalizeRelativePath(path: string): string {
  return path
    .replace(/\\/g, "/")
    .replace(/^\.?\//, "")
    .replace(/\/+$/, "");
}

function coerceToMarkdownPath(path: string): string {
  const normalized = normalizeRelativePath(path);
  const lastSlashIndex = normalized.lastIndexOf("/");
  const dirname =
    lastSlashIndex >= 0 ? normalized.slice(0, lastSlashIndex) : "";
  const basename =
    lastSlashIndex >= 0 ? normalized.slice(lastSlashIndex + 1) : normalized;
  const dotIndex = basename.lastIndexOf(".");

  if (dotIndex > 0) {
    const ext = basename.slice(dotIndex + 1).toLowerCase();
    if (ext === "md" || ext === "mdx") {
      return normalized;
    }
    const coercedBase = `${basename.slice(0, dotIndex)}.md`;
    return dirname ? `${dirname}/${coercedBase}` : coercedBase;
  }

  const coercedBase = `${basename}.md`;
  return dirname ? `${dirname}/${coercedBase}` : coercedBase;
}

function expandFolderPathSegments(path: string): string[] {
  const normalized = normalizeRelativePath(path);
  if (!normalized) return [];
  const parts = normalized.split("/").filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    out.push(parts.slice(0, i + 1).join("/"));
  }
  return out;
}

function mergeVirtualFolders(
  nodes: FileNode[],
  virtualFolderPaths: string[],
): FileNode[] {
  const root: FileNode[] = cloneNodes(nodes);
  const folderMap = new Map<string, FileNode>();

  const visit = (node: FileNode) => {
    if (node.type !== "folder") return;
    folderMap.set(node.path, node);
    for (const child of node.children ?? []) {
      visit(child);
    }
  };
  for (const node of root) {
    visit(node);
  }

  const ensureFolder = (parts: string[]): FileNode => {
    const folderPath = parts.join("/");
    const existing = folderMap.get(folderPath);
    if (existing) return existing;

    const node: FileNode = {
      name: parts[parts.length - 1]!,
      path: folderPath,
      type: "folder",
      children: [],
      isDirty: false,
      hasDirtyDescendant: false,
    };
    folderMap.set(folderPath, node);

    if (parts.length === 1) {
      root.push(node);
    } else {
      const parent = ensureFolder(parts.slice(0, -1));
      const parentChildren = parent.children ?? [];
      parentChildren.push(node);
      parent.children = parentChildren;
    }
    return node;
  };

  for (const virtualPath of virtualFolderPaths) {
    const normalized = normalizeRelativePath(virtualPath);
    if (!normalized) continue;
    const parts = normalized.split("/").filter(Boolean);
    if (parts.length === 0) continue;
    ensureFolder(parts);
  }

  return root;
}

function cloneNodes(nodes: FileNode[]): FileNode[] {
  return nodes.map((node) => ({
    ...node,
    children: node.children ? cloneNodes(node.children) : undefined,
  }));
}

function collectAllFilePaths(nodes: FileNode[]): Set<string> {
  const paths = new Set<string>();
  const visit = (node: FileNode) => {
    if (node.type === "file") {
      paths.add(node.path);
      return;
    }
    for (const child of node.children ?? []) {
      visit(child);
    }
  };
  for (const node of nodes) {
    visit(node);
  }
  return paths;
}

function collectDirtyFilePaths(nodes: FileNode[]): Set<string> {
  const dirtyPaths = new Set<string>();
  const visit = (node: FileNode) => {
    if (node.type === "file") {
      if (node.isDirty) {
        dirtyPaths.add(node.path);
      }
      return;
    }
    for (const child of node.children ?? []) {
      visit(child);
    }
  };
  for (const node of nodes) {
    visit(node);
  }
  return dirtyPaths;
}

function collectDeletedFilePaths(nodes: FileNode[]): Set<string> {
  const deletedPaths = new Set<string>();
  const visit = (node: FileNode) => {
    if (node.type === "file" && node.isDeleted) {
      deletedPaths.add(node.path);
      return;
    }
    for (const child of node.children ?? []) {
      visit(child);
    }
  };
  for (const node of nodes) {
    visit(node);
  }
  return deletedPaths;
}
